import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ReportStatus, Vehicle } from '@app-model/vehicle';
import { DealerService } from '@app-services/dealer.service';
import { OpenService } from '@app-services/open.service';

@Component({
  selector: 'app-purchase-profile-vehicle-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './purchase-profile-vehicle-card.component.html',
  styleUrls: ['./purchase-profile-vehicle-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseProfileVehicleCardComponent implements OnInit {
  readonly vehicle = input.required<Vehicle>();

  private readonly router = inject(Router);
  private readonly dealerService = inject(DealerService);
  private readonly openService = inject(OpenService);

  readonly isFavorited = signal<boolean>(false);

  readonly imageUrl = computed<string | null>(() => {
    const v = this.vehicle();
    const urls = v?.imgUrls;
    if (urls && urls.length > 0) {
      return urls[0];
    }
    return null;
  });

  readonly title = computed<string>(() => {
    const v = this.vehicle();
    const parts = [v.make, v.model].filter(Boolean);
    return parts.join(' ') || 'Veículo';
  });

  readonly subtitle = computed<string>(() => {
    const v = this.vehicle();
    const year =
      v.manufactureYear && v.modelYear
        ? `${v.manufactureYear}/${v.modelYear}`
        : v.modelYear
        ? `${v.modelYear}`
        : '';
    const km =
      typeof v.km === 'number'
        ? `${v.km.toLocaleString('pt-BR')} km`
        : v.km
        ? `${v.km} km`
        : '';
    return [year, km].filter(Boolean).join(' · ');
  });

  readonly priceFormatted = computed<string>(() => {
    const v = this.vehicle();
    const val = v.currentPrice || v.highestBid || 0;
    if (!val) return 'Sob consulta';
    return `R$ ${val.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
  });

  readonly fipeDiscountFormatted = computed<string | null>(() => {
    const v = this.vehicle();
    if (v.fipeDiff === undefined || v.fipeDiff === null) {
      return null;
    }
    const raw = v.fipeDiff;
    const diff = Math.round(Math.abs(raw) <= 1 ? raw * 100 : raw);
    if (diff < 0) {
      return `${Math.abs(diff)}% abaixo FIPE`;
    }
    if (diff > 0) {
      return `${diff}% acima FIPE`;
    }
    return 'Na FIPE';
  });

  readonly isFipeDiscount = computed<boolean>(() => {
    const v = this.vehicle();
    return (v.fipeDiff ?? 0) < 0;
  });

  readonly hasApprovedReport = computed<boolean>(() => {
    const status = this.vehicle()?.reportStatus;
    return status === ReportStatus.APPROVED || (status as unknown as string) === 'APPROVED';
  });

  readonly location = computed<string>(() => {
    const v = this.vehicle();
    const parts = [v.city, v.uf].filter(Boolean);
    return parts.join(' / ') || 'Brasil';
  });

  ngOnInit(): void {
    if (this.vehicle()?.favorite) {
      this.isFavorited.set(true);
    }
  }

  toggleFavorite(event: MouseEvent): void {
    event.stopPropagation();
    const v = this.vehicle();
    const nextState = !this.isFavorited();
    this.isFavorited.set(nextState);
    v.favorite = nextState;

    if (nextState) {
      this.dealerService.saveAsFavorite(v.id, false).subscribe();
    } else {
      this.dealerService.removeAsFavorite(v.id, false).subscribe();
    }
  }

  goToDetails(): void {
    const v = this.vehicle();
    const slug = this.openService.getVehicleSlug(v) || 'veiculo';
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/arrematar', 'veiculos', slug, v.id])
    );
    window.open(url, '_blank', 'noopener');
  }
}
